import { Trans } from "@lingui/react/macro";
import { useMemo } from "react";

import { assetName, useAssets, useStatsSummary } from "@/features/matches";
import { Skeleton } from "@/shared/ui/skeleton";

import type { HeroBuildsSearch } from "../../lib/heroBuildsSearchSchema";
import { type HeroChoice, HeroPicker } from "./HeroPicker";
import { HeroBuildsView } from "./HeroBuildsView";

type HeroBuildsPageProps = {
  search: HeroBuildsSearch;
  onSearchChange: (next: HeroBuildsSearch) => void;
};

// Экран «Сборки» по макету design/prototype/HeroBuilds.dc.html: выбор героя и сборки из игры,
// с которыми играют сильные игроки, по типам от частого к редкому. Герой — в search params;
// без него — самый частый герой игрока, а если сводки ещё нет — первый по алфавиту.
export const HeroBuildsPage = ({ search, onSearchChange }: HeroBuildsPageProps) => {
  const assets = useAssets();
  const stats = useStatsSummary();
  const heroes = useMemo<HeroChoice[]>(
    () =>
      (assets.data?.heroes ?? [])
        .filter((hero) => hero.playable)
        .map((hero) => ({ id: hero.id, name: assetName(hero.name), iconUrl: hero.icon_url }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [assets.data],
  );
  const favorite = stats.data?.summary?.heroes[0]?.hero_id;
  const selectedId =
    [search.hero, favorite, heroes[0]?.id].find(
      (id) => id !== undefined && heroes.some((hero) => hero.id === id),
    ) ?? undefined;
  const selected = heroes.find((hero) => hero.id === selectedId);

  return (
    <main
      data-page="builds"
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-6 px-6 pt-6 pb-16"
    >
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-[36px] leading-tight">
          <Trans>Builds</Trans>
        </h1>
        <p className="text-sm text-muted">
          <Trans>What strong players build on each hero</Trans>
        </p>
      </header>
      {assets.isPending ? (
        <Skeleton className="h-[180px] w-full rounded-xl" />
      ) : (
        <HeroPicker
          heroes={heroes}
          selected={selectedId}
          onSelect={(hero) => {
            onSearchChange({ hero });
          }}
        />
      )}
      {selected !== undefined && <HeroBuildsView key={selected.id} hero={selected} />}
    </main>
  );
};
